

import { makeStyles } from 'tss-react/mui';
import { Box, Dialog, DialogContent, DialogTitle, KeyValue, Typography } from 'web-library';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import React from 'react';
import { Button } from '@mui/material';
import { stringToSemanticDisplay } from '../../../../utils/DateUtils';
import { IdType } from '../../../../models';

const useStyles = makeStyles()((theme) =>
({
  titleContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    marginBottom: theme.spacing(4),
  },
  formElementContainer: {
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
    width: '100%',
  },
  buttonContainer: {
    float: 'right',
    marginBottom: theme.spacing(2),
  },
  confirmChangesDialogUpperContent: {
    marginBottom: theme.spacing(1),
  },
  confirmChangesDialogContent: {
    marginBottom: theme.spacing(3),
  },
}));
const parseIdType = (value: string): string | undefined => {
  if (value === 'I') {
    return IdType.NRIC_OR_FIN;
  }
  if (value === 'P') {
    return IdType.PASSPORT;
  }
  return undefined;
};
const getNewValueFromForm = (newValues: any, oldValues: any) => {
  const differences: { [key: string]: { before?: string; after?: string } }[] = [{}];
  const before = 'before';
  const after = 'after';

  if (newValues.name && newValues.name !== oldValues.name) {
    const changes: { [key: string]: string | undefined } = {};
    changes[before] = oldValues.name;
    changes[after] = newValues.name;
    differences.push({ Name: changes });
  }
  if (newValues.IdType && newValues.IdType !== oldValues.userIdType) {
    const changes: { [key: string]: string | undefined } = {};
    changes[before] = parseIdType(oldValues.userIdType);
    changes[after] = parseIdType(newValues.IdType);
    differences.push({ IDType: changes });
  }
  if (newValues.NricFinPassportNo && newValues.NricFinPassportNo !== oldValues.NricFinPassportNo) {
    const changes: { [key: string]: string | undefined } = {};
    changes[before] = oldValues.NricFinPassportNo;
    changes[after] = newValues.NricFinPassportNo;
    differences.push({ NricFinPassportNo: changes });
  }

  const newDob = stringToSemanticDisplay(newValues.dateOfBirth);
  const oldDob = stringToSemanticDisplay(oldValues.dateOfBirth);
  if (newDob && newDob !== oldDob) {
    const changes: { [key: string]: string | undefined } = {};
    changes[before] = oldDob;
    changes[after] = newDob;
    differences.push({ DateOfBirth: changes });
  }
  if (
    newValues.prStatus &&
    newValues.prStatus.codeValueDescription !== oldValues.prStatus.codeValueDescription
  ) {
    const changes: { [key: string]: string | undefined } = {};
    changes[before] = oldValues.prStatus.codeValueDescription;
    changes[after] = newValues.prStatus.codeValueDescription;
    differences.push({ PrStatus: changes });
  }
  if (
    newValues.citizenship &&
    newValues.citizenship.codeValueDescription !== oldValues.citizenship.codeValueDescription
  ) {
    const changes: { [key: string]: string | undefined } = {};
    changes[before] = oldValues.citizenship.codeValueDescription;
    changes[after] = newValues.citizenship.codeValueDescription;
    differences.push({ CitizenshipType: changes });
  }
  return differences;
};

interface CandidateProfileRequestAmendmentDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  formHook: UseFormReturn;
  singPassRedirectObject?: any;
}

const CandidateProfileRequestAmendmentDialog = ({
  open,
  onClose,
  onConfirm,
  formHook,
  singPassRedirectObject,
}: CandidateProfileRequestAmendmentDialogProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { getValues, control } = formHook;
  const newValues = getValues();
  const oldValues = singPassRedirectObject || control._defaultValues;

  const differences = getNewValueFromForm(newValues, oldValues);

  return (
    <Dialog id="change-candidateProfileRequestAmendment-dialog" fullWidth open={open} maxWidth="md">
      <DialogTitle>
        <Box className={classes.titleContent}>
          <Typography variant="h4">
            {t('profile->>CreateCandidateForm.ConfirmChangesDialogTitle')}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography className={classes.confirmChangesDialogUpperContent}>
          {t('profile->>CreateCandidateForm.ConfirmChangesDialogContent1')}
        </Typography>
        <Typography style={{ fontStyle: 'italic' }} className={classes.confirmChangesDialogContent}>
          {t('profile->>CreateCandidateForm.ConfirmChangesDialogContent2')}
        </Typography>
        <Typography className={classes.title} variant="h5">
          {t('profile->>CreateCandidateForm.ChangeSummary')}
        </Typography>
        {differences.length > 0 &&
          differences.map((difference, _index) =>
            Object.entries(difference).map(([key, changes]) => {
              const { before, after }: { before?: string; after?: string } = changes;
              const oldAndNewValue = [
                t('admin->>ProfileAmendmentRequests.OldValue', {
                  oldValue: before === 'ALIEN' ? 'FOREIGNER' : before,
                }),
                t('admin->>ProfileAmendmentRequests.NewValue', {
                  newValue: after === 'ALIEN' ? 'FOREIGNER' : after,
                }),
              ];
              return (
                <KeyValue
                  key={key}
                  keyText={t(`profile->>CreateCandidateForm.${key}`)}
                  valueText={oldAndNewValue}
                />
              );
            })
          )}
        <Typography>{t('profile->>CreateCandidateForm.ConfirmChangesDialogContent3')}</Typography>
        <Box className={classes.buttonContainer}>
          <Button variant="text" type="button" onClick={onClose}>
            {t('profile->>CreateCandidateForm.Cancel')}
          </Button>
          <Button id="button-candidateProfileDialog-proceed" type="submit" onClick={onConfirm}>
            {t('profile->>CreateCandidateForm.Proceed')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
export default CandidateProfileRequestAmendmentDialog;
